import { useQuery } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { companiesQueryOptions } from '@/entities/company'
import { type EmployeeOption, EmployeePicker } from '@/entities/employee'
import {
  type CreatedMeeting,
  CreateMeetingInputSchema,
  CustomerVisibilityField,
  customerVisibilityBlock,
  MeetingTagsInput,
  projectRequiresCompany,
  useCreateMeetingMutation,
} from '@/entities/meeting'
import { projectsQueryOptions } from '@/entities/project'
import { ApiError } from '@/shared/api'
import { deviceZoneDiffers, momentZone, wallTimeToIso, zoneLabel } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { CompanyProjectFields, type CompanyProjectValue } from '@/shared/ui/company-project-fields'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input, Textarea } from '@/shared/ui/field'
import { toast } from '@/shared/ui/toast'

// Подпись зоны у поля времени: снимает вопрос «какое время вводить». Поле —
// настенное время зоны вашего устройства: набранное «15:00» станет моментом по
// вашим часам, а коллеге в другой зоне покажется его цифрами. Когда зона
// устройства разошлась с зоной организации, это проговаривается прямо — цифры в
// поле и цифры в договорённости команды в этот момент разные.
function scheduleHint(): string {
  const tz = momentZone()
  const base = `Оставьте пустым, чтобы начать сразу · время в зоне ${zoneLabel(tz)}`
  return deviceZoneDiffers() ? `${base} — вашего устройства` : base
}

interface CreateMeetingDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (meeting: CreatedMeeting) => void
}

export function CreateMeetingDialog({ open, onClose, onCreated }: CreateMeetingDialogProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [invitees, setInvitees] = useState<EmployeeOption[]>([])
  const [isPrivate, setIsPrivate] = useState(false)
  const [customerVisible, setCustomerVisible] = useState(false)
  const privateFieldId = useId()
  const [companyProject, setCompanyProject] = useState<CompanyProjectValue>({
    companyId: '',
    projectId: '',
  })

  // Справочники хаба для пикера: проекты — только выбранной компании.
  const companies = useQuery(companiesQueryOptions())
  const projects = useQuery({
    ...projectsQueryOptions(companyProject.companyId),
    enabled: companyProject.companyId !== '',
  })
  const [scheduledAt, setScheduledAt] = useState('')
  const [titleError, setTitleError] = useState<string>()
  const [cpError, setCpError] = useState<string>()
  const mutation = useCreateMeetingMutation()

  // Инвариант «клиенту — только неприватную встречу с компанией» виден прямо в
  // форме: чекбокс блокируется с объяснением, а не ловит 422 после отправки.
  const visibilityBlock = customerVisibilityBlock({
    private: isPrivate,
    companyId: companyProject.companyId,
  })

  function close() {
    setTitle('')
    setDescription('')
    setTags([])
    setInvitees([])
    setIsPrivate(false)
    setCustomerVisible(false)
    setCompanyProject({ companyId: '', projectId: '' })
    setScheduledAt('')
    setTitleError(undefined)
    setCpError(undefined)
    onClose()
  }

  function handleSubmit() {
    setCpError(undefined)
    if (projectRequiresCompany(companyProject.companyId, companyProject.projectId)) {
      setCpError('Проект нельзя указать без компании')
      return
    }
    const parsed = CreateMeetingInputSchema.safeParse({
      title: title.trim(),
      description: description.trim() || undefined,
      tags: tags.length > 0 ? tags : undefined,
      companyId: companyProject.companyId || undefined,
      projectId: companyProject.projectId || undefined,
      invitees: invitees.length > 0 ? invitees.map((i) => i.subject) : undefined,
      private: isPrivate || undefined,
      // Пометка сгорает, если по ходу заполнения формы стала невозможной
      // (включили приватность, сняли компанию) — чекбокс в этот момент уже
      // заблокирован и показывает причину.
      customerVisible: (customerVisible && !visibilityBlock) || undefined,
      // datetime-local отдаёт настенное время без зоны; моментом его делает
      // зона того, кто набрал, — зона его устройства (дефолт wallTimeToIso).
      scheduledAt: scheduledAt ? wallTimeToIso(scheduledAt) : undefined,
    })
    if (!parsed.success) {
      setTitleError(parsed.error.issues.find((i) => i.path[0] === 'title')?.message)
      return
    }
    mutation.mutate(parsed.data, {
      onSuccess: (created) => {
        toast.success('Встреча создана')
        onCreated(created)
      },
      onError: (err) => {
        // Бэк — последний арбитр инварианта: 422 PROJECT_REQUIRES_COMPANY на поле.
        if (err instanceof ApiError && err.code === 'PROJECT_REQUIRES_COMPANY') {
          setCpError('Проект нельзя указать без компании')
          return
        }
        toast.error('Не удалось создать встречу')
      },
    })
  }

  return (
    <Dialog open={open} onClose={close} title="Новая встреча">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
        className="flex flex-col gap-4"
      >
        <Field label="Название" {...(titleError ? { error: titleError } : {})}>
          {(p) => (
            <Input
              {...p}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Планёрка команды"
            />
          )}
        </Field>
        <Field label="Описание" hint="Опционально: повестка или контекст встречи">
          {(p) => (
            <Textarea
              {...p}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="О чём встреча"
              maxLength={2000}
            />
          )}
        </Field>
        <Field label="Приглашённые" hint="Кого позвать. Список можно поменять и после создания">
          {(p) => <EmployeePicker {...p} value={invitees} onChange={setInvitees} />}
        </Field>
        <div className="flex items-start gap-3 rounded-control bg-bg-surface px-4 py-3">
          <input
            id={privateFieldId}
            type="checkbox"
            className="mt-0.5"
            checked={isPrivate}
            onChange={(e) => setIsPrivate(e.target.checked)}
          />
          <label htmlFor={privateFieldId} className="text-sm">
            <span className="text-text-primary">Скрыть от посторонних</span>
            <span className="block text-text-tertiary">
              Встречу увидят только организатор, участники и приглашённые. Видимость можно поменять
              и позже.
            </span>
          </label>
        </div>
        <CustomerVisibilityField
          checked={customerVisible && !visibilityBlock}
          onChange={setCustomerVisible}
          companyName={
            companies.data?.find((c) => c.id === companyProject.companyId)?.name ??
            companyProject.companyId
          }
          {...(visibilityBlock ? { block: visibilityBlock } : {})}
        />
        <Field label="Теги" hint="Тема встречи. Если пусто, встреча внутренняя">
          {(p) => <MeetingTagsInput {...p} value={tags} onChange={setTags} />}
        </Field>
        <CompanyProjectFields
          value={companyProject}
          onChange={setCompanyProject}
          companies={companies.data ?? []}
          projects={projects.data ?? []}
          companiesLoading={companies.isPending}
          projectsLoading={projects.isPending}
          error={cpError}
        />
        <Field label="Запланировать" hint={scheduleHint()}>
          {(p) => (
            <Input
              {...p}
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
          )}
        </Field>
        <div className="mt-2 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={close}>
            Отмена
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Создание…' : 'Создать'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
